import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import { Fab, Tooltip } from "@material-ui/core";
import CopyIcon from '@material-ui/icons/FileCopy';
import KitDetails from "./KitDetails";
import DataPanel from "../DataPanel";
import KitsPrintoutButton from "./KitsPrintoutButton";
import { useCallback } from "react";
import { KitStatus } from "./KitStatus";
import { PackagingType } from "../../api/KitApi";
import { formatCurrency } from "../../engine/PriceUtilities";
import KitsCleanupButton from "./KitsCleanupButton";
import useHttp from "../../security/Http.hook";
import RemoveKitWithFormsButton from "./RemoveKitWithFormsButton";

interface Props {
  readonly customerId: number;
  readonly priceListId: number;
}

interface KitStatusContext {
  customerId: number;
  kitId: number;
  timestamp: Date;
  packagingType: PackagingType;
}

const renderKitStatusCellBody = (context: KitStatusContext) => {
  const customerId = context.customerId;
  const kitId = context.kitId;
  const timestamp = context.timestamp;
  return <KitStatus
    key={ `${customerId}/${kitId}/${timestamp.getTime()}` }
    customerId={ customerId }
    kitId={ kitId }
    packagingType={ context.packagingType }
    timestamp={ timestamp }
  />;
};

const columns = [
  {
    name: 'name',
    label: 'Nazwa',
    options: {}
  },
  {
    name: 'packagingType',
    label: 'Sposób pakowania',
    options: {}
  },
  {
    name: 'packagingPrice',
    label: 'Cena pakowania',
    options: {}
  },
  {
    name: 'weight',
    label: 'Waga całkowita',
    options: {}
  },
  {
    name: 'total',
    label: 'Cena całkowita',
    options: {}
  },
  {
    name: 'status',
    label: 'Status',
    options: {
      customBodyRender: renderKitStatusCellBody
    }
  }
];

const getDisplayedPackagingType = (packagingType: PackagingType) => {
  switch (packagingType) {
    case 'specification' : return 'zgodnie ze specyfikacją';
    case 'string' : return 'wiązanie sznurkiem';
    case 'foil' : return 'folia';
    case 'pallet' : return 'paleta';
    case 'stretch' : return 'folia stretch';
    case 'none' : return 'brak';
    default : return '<?>';
  }
};

const KitsPanel = (props: Props) => {
  const http = useHttp();
  const customerId = props.customerId;
  const priceListId = props.priceListId;

  const handleCopyKitButtonClicked = (kitId: number, refreshKits: () => void) => {
    const kitData = {
      name: `Kopia [${kitId}]`
    }
    http
      .post(`/api/customers/${customerId}/kits/${kitId}/copies`, kitData)
      .then(refreshKits);
  };

  const renderCustomKitButtons = (
    buttonClasses: ClassNameMap,
    getSelectedIds: () => number[],
    refreshKits: () => void,
    reportAlerts: (messages: string[]) => void
  ) => {
    const selectedKitIds = getSelectedIds();
    return <>
      <RemoveKitWithFormsButton
        classes={ buttonClasses }
        customerId={ customerId }
        priceListId={ priceListId }
        kitId={ selectedKitIds.length === 1 ? selectedKitIds[0] : null }
        reportAlert={ (message: string) => reportAlerts([message]) }
        onKitRemoved={ refreshKits }
      />
      <KitsPrintoutButton
        customerId={ customerId }
        priceListId={ priceListId }
        kitIds={ selectedKitIds }
        buttonClasses={ buttonClasses }
      />
      <Tooltip title="Wykonaj kopię wybranego kompletu">
        <Fab
          aria-label="copy"
          className={buttonClasses.button}
          color="primary"
          disabled={selectedKitIds.length !== 1}
          size="medium"
          onClick={() => handleCopyKitButtonClicked(selectedKitIds[0], refreshKits)}
        >
          <CopyIcon/>
        </Fab>
      </Tooltip>
      <KitsCleanupButton
        buttonClasses={ buttonClasses }
        customerId={ customerId }
        reportAlerts={ reportAlerts }
        refreshKits={ refreshKits }
        />
    </>
  };

  const convertKits = useCallback((data: any) => ({
    ...data,
    packagingType: getDisplayedPackagingType(data.packagingType),
    total: formatCurrency(data.elementsPrice + data.packagingPrice),
    status: ({
      customerId: customerId,
      kitId: data.id,
      packagingType: data.packagingType,
      timestamp: new Date() }),
  }), [customerId]);

  return <>
    <DataPanel
      title='Komplety'
      resource={ `customers/${customerId}/price-lists/${priceListId}/kits` }
      columns={ columns }
      deleteRequiresConfirmation
      selectableRows='multiple'
      tableSearchEnabled
      renderDetails={(detailsProps) => <KitDetails { ...{ ...detailsProps, customerId, priceListId }} />}
      renderCustomButtons={ renderCustomKitButtons }
      convert={ convertKits }
    />
  </>
};
export default KitsPanel;